<script setup>
  import { onMounted, ref, computed, watch, toRaw } from "vue";
  import { storeToRefs } from "pinia";
  import { watchDebounced } from "@vueuse/core";
  import { useGoogleMaps } from "@utils/composables/googleMaps";
  import { useLocationsStore } from "@stores/mAudience/locations";

  const { getMAudienceCountry, getMAudienceLocations } = storeToRefs(useLocationsStore());
  const { countryCodes, map, geocoder, initialized, initializeGoogleMaps } = useGoogleMaps();

  const markers = ref([]);
  const circles = ref([]);

  const countryName = computed(() => countryCodes.value?.[getMAudienceCountry.value] || "France");

  const setBoundsToCountry = async () => {
    if (!initialized.value) return false;

    geocoder.value.geocode({ address: countryName.value }, (results, status) => {
      if (status !== "OK" || !results[0]) console.error(status);

      map.value.setCenter(results[0].geometry.location);
      map.value.fitBounds(results[0].geometry.viewport);
    });
  };

  const positionMarkersFromLocations = async () => {
    if (!initialized.value) return false;

    await clearExistingMarkers();
    markers.value = getMAudienceLocations.value
      ?.filter((location) => location.showOnMap)
      ?.map(
        (location) =>
          new window.google.maps.Marker({
            position: location.geocoded.geometry.location,
            icon: {
              path: "M11 2c-3.9 0-7 3.1-7 7 0 5.3 7 13 7 13 0 0 7-7.7 7-13 0-3.9-3.1-7-7-7Zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5 0-1.4 1.1-2.5 2.5-2.5 1.4 0 2.5 1.1 2.5 2.5 0 1.4-1.1 2.5-2.5 2.5Z",
              scale: 1.5,
              anchor: new window.google.maps.Point(11, 22),
              fillOpacity: 1,
              fillColor: "#D22B2B",
              strokeOpacity: 0,
            },
            map: map.value,
          }),
      );
    circles.value = getMAudienceLocations.value
      ?.filter((location) => location.showOnMap && location.has_radius && location.radius > 0)
      ?.map((location) => {
        let center = undefined;
        const viewport = location.geocoded.geometry.viewport;
        if (viewport.east) {
          center = {
            lat: (Number(viewport.north) + Number(viewport.south)) / 2,
            lng: (Number(viewport.east) + Number(viewport.west)) / 2,
          };
        } else {
          center = viewport.getCenter().toJSON();
        }
        return new window.google.maps.Circle({
          strokeColor: "#D22B2B",
          strokeOpacity: 0.15,
          strokeWeight: 1,
          fillColor: "#D22B2B",
          fillOpacity: 0.15,
          center,
          radius: Number(location.radius) * 1000,
          map: map.value,
        });
      });
  };

  const clearExistingMarkers = async () => {
    markers.value?.forEach(async (marker) => await toRaw(marker).setMap(null));
    circles.value?.forEach(async (circle) => await toRaw(circle).setMap(null));
  };

  onMounted(async () => {
    await initializeGoogleMaps();
    await setBoundsToCountry();
    await positionMarkersFromLocations();
  });

  watch(getMAudienceCountry, setBoundsToCountry);
  watchDebounced(getMAudienceLocations, () => positionMarkersFromLocations(), { debounce: 500, deep: true });
</script>

<template>
  <div class="px-2">
    <div
      id="map"
      ref="el"
      class="m-1 h-[50vh] rounded" />
  </div>
</template>
